import { Injectable } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { SalesSupportActionsAttributeFilter } from '../app/shared/ui/models/sales-support-actions-attribute-filter';
import { AttributeFilter } from '../app/shared/ui/models/attribute-filter';
import { StringAttributeFilter } from '../app/shared/ui/models/string-attribute-filter';
import { DatetimeAttributeFilter } from '../app/shared/ui/models/datetime-attribute-filter';
import { PaiementMethodAttributeFilter } from '../app/shared/ui/models/paiement-method-attribute-filter';
import { TransporterMemberAttributeFilter } from '../app/shared/ui/models/transporter-member-attribute-filter';
import { DateAttributeFilter } from '../app/shared/ui/models/date-attribute-filter';
import { NumberAttributeFilter } from '../app/shared/ui/models/number-attribute-filter';
import { DeliveryStatusAttributeFilter } from '../app/shared/ui/models/delivery-status-attribute-filter';
import { PaymentStatusAttributeFilter } from '../app/shared/ui/models/payment-status-attribute-filter';
import { RefundedStatusAttributeFilter } from '../app/shared/ui/models/refunded-status-attribute-filter';

@Injectable({
  providedIn: 'root',
})
export class BaryFilterFactoryService {
  constructor(public translateService: TranslateService) {}

  public getCustomersFilters(): AttributeFilter[] {
    return [
      new StringAttributeFilter('userCompanyName', 'Client', 'contains'),
      new StringAttributeFilter('userFirstName', 'Prénom', 'contains'),
      new StringAttributeFilter('userLastName', 'Nom', 'contains'),
      new StringAttributeFilter('userCompanyAddressZipCode', 'Code postal', 'contains'),
      new DatetimeAttributeFilter('creationDate', 'Date de création', 'equals'),
      new PaiementMethodAttributeFilter('userTypology', 'Moyen de paiement', 'equals', undefined, undefined, this.translateService),
      new TransporterMemberAttributeFilter('transporterMemberId', 'Commercial', 'equals'),
    ];
  }

  public getTransporterMembersFilters(): AttributeFilter[] {
    return [
      new StringAttributeFilter('firstName', 'Prénom', 'contains'),
      new StringAttributeFilter('lastName', 'Nom', 'contains'),
      new StringAttributeFilter('mail', 'E-mail', 'contains'),
    ];
  }

  public getServicesFilters(): AttributeFilter[] {
    return [new StringAttributeFilter('name', 'Service', 'contains')];
  }

  public getNotificationsFilters(): AttributeFilter[] {
    return [
      new StringAttributeFilter('type', 'Service', 'contains'),
      new StringAttributeFilter('content', 'Contenu', 'contains'),
      new StringAttributeFilter('startDate', 'Date de début', 'contains'),
      new StringAttributeFilter('endDate', 'Date de fin', 'contains'),
    ];
  }

  public getTransportsFilters(): AttributeFilter[] {
    return [
      new NumberAttributeFilter('id', 'OT', 'equals'),
      new DateAttributeFilter('pickupDate', "Date d'expédition", 'equals'),
      new StringAttributeFilter('pickupReference', "Référence d'expédition", 'equals'),
      new StringAttributeFilter('pickupAddressCity', "Ville d'expédition", 'contains'),
      new DateAttributeFilter('deliveryDate', 'Date de livraison', 'equals'),
      new StringAttributeFilter('deliveryReference', 'Référence de livraison', 'equals'),
      new StringAttributeFilter('deliveryAddressCity', 'Ville de livraison', 'contains'),
      new NumberAttributeFilter('totalVolume', 'Volume total', 'equals'),
      new NumberAttributeFilter('totalWeight', 'Poids total', 'equals'),
      // TODO: change when new offer feature is up: new StringAttributeFilter('offer', "Type d'offre", 'equals'),
      new DeliveryStatusAttributeFilter('deliveryStatus', 'Statut', 'in'),
    ];
  }

  public getQuotationFilters(): AttributeFilter[] {
    return [
      new StringAttributeFilter('pickupAddressCity', "Ville d'expédition", 'contains'),
      new StringAttributeFilter('deliveryAddressCity', 'Ville de livraison', 'contains'),
      new DateAttributeFilter('pickupDate', "Date d'expédition", 'equals'),
      new DateAttributeFilter('deliveryDate', 'Date de livraison', 'equals'),
      new StringAttributeFilter('pickupReference', "Référence d'expédition", 'equals'),
      new NumberAttributeFilter('totalVolume', 'Volume total', 'equals'),
      new NumberAttributeFilter('totalWeight', 'Poids total', 'equals'),
    ];
  }

  public getBillsForModeratorFilters(): AttributeFilter[] {
    return [...this.getBillsForCustomerFilters(), new StringAttributeFilter('userCompanyName', 'Client', 'contains')];
  }

  public getBillsForCustomerFilters(): AttributeFilter[] {
    return [
      new StringAttributeFilter('billNumber', 'Numéro de facture', 'contains'),
      new DateAttributeFilter('billDate', 'Date de facturation', 'equals'),
      new NumberAttributeFilter('price', 'Total TTC', 'equals'),
      new PaymentStatusAttributeFilter('status', 'Statut', 'in', undefined, undefined, this.translateService),
      new RefundedStatusAttributeFilter('refundedStatus', 'Statut de remboursement', 'in'),
      new PaiementMethodAttributeFilter('paymentMethod', 'Paiement', 'equals', undefined, undefined, this.translateService),
    ];
  }

  public getRefundsFilters(): AttributeFilter[] {
    return [
      new StringAttributeFilter('billNumber', this.translateService.instant('refund.filters.billNumber'), 'contains'),
      new StringAttributeFilter('userCompanyName', this.translateService.instant('refund.filters.userCompanyName'), 'contains'),
      new DateAttributeFilter('billDate', this.translateService.instant('refund.filters.billDate'), 'equals'),
      new NumberAttributeFilter('price', this.translateService.instant('refund.filters.price'), 'equals'),
    ];
  }

  public getPricingUploadFilters(): AttributeFilter[] {
    return [
      new StringAttributeFilter('pickupCountry', "Pays d'enlèvement", 'equals'),
      new StringAttributeFilter('pickupZipCodeData', "Règle d'enlèvement", 'equals'),
      new StringAttributeFilter('deliveryCountry', 'Pays de livraison', 'equals'),
      new StringAttributeFilter('deliveryZipCodeData', 'Règle de livraison', 'equals'),
    ];
  }

  public getAcceptanceFilters(): AttributeFilter[] {
    return [
      new StringAttributeFilter('pickupCountry', "Pays d'enlèvement", 'equals'),
      new StringAttributeFilter('pickupZipCodeData', "Règle d'enlèvement", 'equals'),
      new StringAttributeFilter('deliveryCountry', 'Pays de livraison', 'equals'),
      new StringAttributeFilter('deliveryZipCodeData', 'Règle de livraison', 'equals'),
      new NumberAttributeFilter('acceptanceDays', 'Délai (j)', 'equals'),
    ];
  }

  public getOfferFilters(): AttributeFilter[] {
    return [
      new NumberAttributeFilter('id', 'Identifiant', 'equals'),
      new StringAttributeFilter('name', 'Nom', 'equals'),
      new StringAttributeFilter('measureUnit', 'Unité', 'equals'),
    ];
  }

  public getOfferInsuranceItemsFilters(): AttributeFilter[] {
    return [
      new StringAttributeFilter('itemsNature', 'Type de marchandise', 'contains'),
      new NumberAttributeFilter('maxValue', 'Valeur maximum', 'equals'),
      new NumberAttributeFilter('basePricing', 'Prix de base', 'equals'),
      new NumberAttributeFilter('insuranceRate', "Taux d'assurance", 'equals'),
      new NumberAttributeFilter('pricingRate', 'Taux tarifaire', 'equals'),
    ];
  }

  public getUsersFilters(): AttributeFilter[] {
    return [
      new NumberAttributeFilter('id', 'Identifiant', 'equals'),
      new StringAttributeFilter('login', 'Login', 'contains'),
      new StringAttributeFilter('email', 'email', 'contains'),
      new StringAttributeFilter('companyName', "Nom d'organisation", 'contains'),
      new DatetimeAttributeFilter('creationDate', 'Créé le', 'equals'),
      new DatetimeAttributeFilter('lastModifiedDate', 'Date de dernière modif', 'equals'),
    ];
  }

  public getSpecialDestinationFilters(): AttributeFilter[] {
    return [
      new StringAttributeFilter('country', 'Pays', 'equals'),
      new StringAttributeFilter('inseeCode', 'Code insee', 'contains'),
      new StringAttributeFilter('zipCode', 'Code postal', 'contains'),
      new StringAttributeFilter('city', 'Ville', 'contains'),
    ];
  }

  public getAddressFilters(): AttributeFilter[] {
    return [
      new StringAttributeFilter('alias', 'Alias', 'contains'),
      new StringAttributeFilter('fullname', 'Entreprise', 'contains'),
      new StringAttributeFilter('zipCode', 'Code postal', 'contains'),
      new StringAttributeFilter('city', 'Ville', 'contains'),
      new StringAttributeFilter('contactFirstName', 'Contact', 'contains'),
    ];
  }

  public getUnvalidatedBasketsFilters(): AttributeFilter[] {
    return [
      new DatetimeAttributeFilter('creationDate', 'Créé le', 'equals'),
      new StringAttributeFilter('clientName', 'Nom du client', 'contains'),
      new DateAttributeFilter('pickupDate', "Date d'expédition", 'equals'),
      new DateAttributeFilter('deliveryDate', 'Date de livraison', 'equals'),
      new StringAttributeFilter('pickupAddressZipCode', "Code Postal d'enlèvement", 'contains'),
      new StringAttributeFilter('pickupAddressCity', "Ville d'enlèvement", 'contains'),
      new StringAttributeFilter('deliveryAddressZipCode', 'Code postal de réception', 'contains'),
      new StringAttributeFilter('deliveryAddressCity', 'Ville de réception', 'contains'),
      new SalesSupportActionsAttributeFilter('typeSalesSupportActions', 'Statut', 'in'),
    ];
  }
}
